import { useState, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  X,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info,
  RefreshCw,
  Loader2,
  Undo2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUiStore, selectToasts, type ToastNotification } from '@/stores/useUiStore'
import { Button } from './button'

const toastConfig = {
  success: { icon: CheckCircle, iconClassName: 'text-success' },
  error: { icon: AlertCircle, iconClassName: 'text-destructive' },
  warning: { icon: AlertTriangle, iconClassName: 'text-warning' },
  info: { icon: Info, iconClassName: 'text-primary' },
}

function Toast({ id, type, title, message, onRetry, onAction, actionLabel }: ToastNotification) {
  const dismissToast = useUiStore((state) => state.dismissToast)
  const [isRetrying, setIsRetrying] = useState(false)
  const [isActioning, setIsActioning] = useState(false)
  const config = toastConfig[type]
  const Icon = config.icon

  const handleRetry = useCallback(async () => {
    if (!onRetry) return
    setIsRetrying(true)
    try {
      await onRetry()
      dismissToast(id)
    } catch {
      // Keep toast visible on retry failure
    } finally {
      setIsRetrying(false)
    }
  }, [onRetry, dismissToast, id])

  const handleAction = useCallback(async () => {
    if (!onAction) return
    setIsActioning(true)
    try {
      await onAction()
      dismissToast(id)
    } catch {
      // Keep toast visible on action failure
    } finally {
      setIsActioning(false)
    }
  }, [onAction, dismissToast, id])

  return (
    <div
      className={cn(
        'bg-popover text-popover-foreground border-border pointer-events-auto flex w-full max-w-[24rem] items-start gap-3 rounded-lg border p-4 shadow-lg transition-all'
      )}
      role="alert"
      data-testid={`toast-${id}`}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', config.iconClassName)} />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium">{title}</p>
        {message && <p className="text-sm opacity-90">{message}</p>}
        {onRetry && type === 'error' && (
          <Button
            variant="ghost"
            size="xs"
            onClick={handleRetry}
            disabled={isRetrying}
            className="mt-2 h-7 px-2 text-xs"
            data-testid={`toast-retry-${id}`}
          >
            {isRetrying ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-3 w-3" />
            )}
            Retry
          </Button>
        )}
        {onAction && actionLabel && (
          <Button
            variant="ghost"
            size="xs"
            onClick={handleAction}
            disabled={isActioning}
            className="mt-2 h-7 px-2 text-xs"
            data-testid={`toast-action-${id}`}
          >
            {isActioning ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Undo2 className="mr-1 h-3 w-3" />
            )}
            {actionLabel}
          </Button>
        )}
      </div>
      <button
        onClick={() => dismissToast(id)}
        className="rounded p-1 opacity-70 hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
        data-testid={`toast-dismiss-${id}`}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Dismiss</span>
      </button>
    </div>
  )
}

/**
 * Toasts sit at the top (below the notch on phones) so they never cover the
 * composer or the keyboard, and clear on every route change.
 */
export function ToastContainer() {
  const toasts = useUiStore(selectToasts)
  const dismissToast = useUiStore((state) => state.dismissToast)
  const { pathname } = useLocation()
  const lastPathname = useRef(pathname)

  useEffect(() => {
    if (lastPathname.current === pathname) return
    lastPathname.current = pathname
    for (const toast of useUiStore.getState().toasts) dismissToast(toast.id)
  }, [pathname, dismissToast])

  if (toasts.length === 0) {
    return null
  }

  return (
    // z-[70]: above dialogs and sheets (z-50, portalled later in the DOM), so a
    // refusal raised from a full-height phone sheet is seen, not hidden under it.
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex flex-col items-center gap-2 p-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] md:top-14 md:right-0 md:left-auto md:items-end md:pt-3"
      data-testid="toast-container"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} />
      ))}
    </div>
  )
}

// Hook for showing API error toasts with retry functionality
export function useApiErrorToast() {
  const showToast = useUiStore((state) => state.showToast)

  const showApiError = (
    message: string,
    options?: {
      title?: string
      duration?: number
      onRetry?: () => void | Promise<void>
    }
  ) => {
    showToast({
      type: 'error',
      title: options?.title || 'Request Failed',
      message,
      duration: options?.duration ?? 0, // Don't auto-dismiss errors with retry
      onRetry: options?.onRetry,
    })
  }

  return { showApiError }
}
